/**
 * URL Sanitizer - Removes OAuth-related query parameters from the URL
 * This prevents "stale" OAuth errors/tokens from persisting in native HashRouter URLs
 */

const OAUTH_PARAMS = [
  'access_token',
  'refresh_token',
  'error',
  'error_description',
  'code',
  'state',
  'deeplink_scheme',
  'redirect',
  'token_type',
  'expires_in',
  'expires_at',
  'provider_token',
  'provider_refresh_token',
];

/**
 * Removes OAuth-related query parameters from window.location
 * Preserves hash routes (e.g., #/home) and non-OAuth query params
 */
export function sanitizeOAuthParams(): void {
  try {
    const url = new URL(window.location.href);
    let modified = false;

    // Remove OAuth params from search (query string)
    OAUTH_PARAMS.forEach(param => {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        modified = true;
      }
    });

    // Also check hash for OAuth params (Supabase sometimes puts them there)
    if (url.hash) {
      // Parse the hash - could be "#/route?params" or "#access_token=xxx"
      const hashContent = url.hash.substring(1);
      
      // Check if hash contains OAuth params directly (not as a route)
      if (hashContent.includes('access_token=') || hashContent.includes('error=')) {
        // This is an OAuth response in the hash, not a route
        // Extract route if present (e.g., "/home" from "#/home?access_token=...")
        const routeMatch = hashContent.match(/^(\/[^?]*)/);
        if (routeMatch) {
          // Keep only the route part
          url.hash = routeMatch[1];
          modified = true;
        } else {
          // No route, just OAuth params - clear the hash
          url.hash = '';
          modified = true;
        }
      }
    }

    if (modified) {
      console.log('[urlSanitizer] Cleaning OAuth params from URL');
      window.history.replaceState({}, '', url.toString());
    }
  } catch (e) {
    console.warn('[urlSanitizer] Failed to sanitize URL:', e);
  }
}

/**
 * Check if the current URL has any OAuth-related parameters
 */
export function hasOAuthParams(): boolean {
  try {
    const url = new URL(window.location.href);
    
    // Check search params
    for (const param of OAUTH_PARAMS) {
      if (url.searchParams.has(param)) {
        return true;
      }
    }
    
    // Check hash for OAuth params
    if (url.hash) {
      const hashContent = url.hash.substring(1);
      if (hashContent.includes('access_token=') || 
          hashContent.includes('error=') ||
          hashContent.includes('code=')) {
        return true;
      }
    }
    
    return false;
  } catch {
    return false;
  }
}
